/* eslint-disable max-len */
import React, { createContext, FC, useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Components } from 'src/configs/components/components';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { getCategoryPage } from 'src/services/cmsApi';
import { CmsPageContent } from 'src/types/cms/cmsCommonTypes';
import { CmsCategoryPageFields } from 'src/types/cms/cmsPageTypes';
import '../styles.scss';

type Props = {
  categorySlug: string;
  type: string;
  categoryName: string;
  settings: {
    firstSection: { [key: string]: any };
    secondSection: { [key: string]: any };
    thirdSection: { [key: string]: any };
  };
};

export const CategoryPageContext = createContext<CmsPageContent<CmsCategoryPageFields> | null>(null);

const CategoryPageConstructor: FC<Props> = ({ settings, type, categorySlug }) => {
  const sessionRecorder = useSessionRecorder();
  const { i18n } = useTranslation();
  const [categoryPage, setCategoryPage] = useState<CmsPageContent<CmsCategoryPageFields> | null>(null);

  const renderCategoryPageWidgets = (line: any): JSX.Element => {
    const orderIndex: any = {};

    Object.keys(line).forEach((key: string) => (orderIndex[`${key}`] = line[key].priority));

    const widgetLine = Object.keys(line)
      .sort((a: string, b: string) => orderIndex[a] - orderIndex[b])
      .filter((key: string) => key !== 'styles')
      .map((key: string, index: number) => {
        const Component = Components[key];
        if (!Component) {
          return null;
        }

        return React.createElement(Component, {
          settings: line[key],
          type,
          key: `${key}_${index}`,
        });
      });

    return <>{widgetLine}</>;
  };

  useEffect(() => {
    sessionRecorder?.trackEvent('Category page view', {
      categorySlug,
    });
  }, []);

  useEffect(() => {
    getCategoryPage(i18n.language, categorySlug).then((res) => {
      setCategoryPage(res.result?.content || null);
    });
  }, [categorySlug, i18n.language]);

  return (
    <CategoryPageContext.Provider value={categoryPage}>
      <div className={'page_container'}>
        <HelmetProvider>
          <Helmet>
            {categoryPage?.meta.pageTitle && <title>{categoryPage?.meta.pageTitle}</title>}
            {categoryPage?.meta.pageDescription && (
              <meta name="description" content={categoryPage?.meta.pageDescription} />
            )}
            {categoryPage?.meta.pageKeywords && <meta name="keywords" content={categoryPage?.meta.pageKeywords} />}
          </Helmet>
        </HelmetProvider>
        <div className={'page_container_first_section'}>{renderCategoryPageWidgets(settings?.firstSection)}</div>
        <div className={'page_container_second_section'} style={settings?.secondSection.styles}>
          {renderCategoryPageWidgets(settings?.secondSection)}
        </div>
        <div className={'page_container_third_section'}>{renderCategoryPageWidgets(settings?.thirdSection)}</div>
      </div>
    </CategoryPageContext.Provider>
  );
};
export default CategoryPageConstructor;
